import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUsers, FaBuilding, FaTruck, FaProjectDiagram, FaFileInvoiceDollar, FaArrowUp, FaArrowDown, FaMinus, FaUserPlus, FaHandshake, FaCheckCircle } from 'react-icons/fa';
import { colors, shadows } from '../../employees/styles/personalakteStyles';
import BusinessPartnerSummaryService, { ContactsSummary } from '../services/BusinessPartnerSummaryService';
import { CHART_COLORS, CHART_SEMANTIC } from '../../../utils/chartColors';

interface ContactsDashboardProps {
  summary: ContactsSummary;
  onFilterChange?: (kind: 'CUSTOMER' | 'PARTNER' | 'SUPPLIER' | null) => void;
  onStageFilterChange?: (stage: 'leads' | 'customers') => void;
}

export default function ContactsDashboard({ summary, onFilterChange, onStageFilterChange }: ContactsDashboardProps): React.JSX.Element {
  const navigate = useNavigate();
  const [hoveredSegment, setHoveredSegment] = useState<string | null>(null);

  const delta = summary.customers.new_this_month - summary.customers.new_last_month;
  const deltaDirection = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';

  const leadsTotal = summary.stages.LEAD + summary.stages.PROSPECT;
  const customersTotal = summary.stages.ACTIVE + summary.stages.INACTIVE;

  const donutSegments = [
    { key: 'CUSTOMER', label: 'Customers', value: summary.counts.CUSTOMER, color: CHART_COLORS.navy },
    { key: 'PARTNER', label: 'Partners', value: summary.counts.PARTNER, color: CHART_COLORS.purple },
    { key: 'SUPPLIER', label: 'Suppliers', value: summary.counts.SUPPLIER, color: CHART_COLORS.pink },
  ];

  const total = summary.counts.total || 1;
  const radius = 70;
  const strokeWidth = 20;
  const centerX = 90;
  const centerY = 90;
  let currentAngle = -90;

  const arcs = donutSegments.map((segment) => {
    const percentage = segment.value / total;
    const angle = percentage * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const x1 = centerX + radius * Math.cos(startRad);
    const y1 = centerY + radius * Math.sin(startRad);
    const x2 = centerX + radius * Math.cos(endRad);
    const y2 = centerY + radius * Math.sin(endRad);

    const largeArcFlag = angle > 180 ? 1 : 0;
    const pathD = angle > 0.5 ? `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}` : '';

    return { ...segment, pathD, percentage: percentage * 100 };
  });

  const displayMonths = summary.customers.new_by_month.slice(-6);
  const maxMonthCount = Math.max(...displayMonths.map((m) => m.count), 1);

  const paymentTotal = summary.payment_behavior.good + summary.payment_behavior.ok + summary.payment_behavior.late;
  const paymentSegments = [
    { key: 'good', label: 'Good', value: summary.payment_behavior.good, color: CHART_COLORS.success },
    { key: 'ok', label: 'OK', value: summary.payment_behavior.ok, color: CHART_COLORS.yellow },
    { key: 'late', label: 'Late', value: summary.payment_behavior.late, color: CHART_COLORS.danger },
  ];

  return (
    <div style={{ marginBottom: '24px' }}>
      {/* Row 1: Sales Pipeline KPIs */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '16px',
          marginBottom: '16px',
        }}
      >
        {/* Total Contacts */}
        <div
          style={{
            background: colors.bgCard,
            borderRadius: 14,
            padding: '20px 24px',
            border: `1px solid ${colors.border}`,
            boxShadow: shadows.card,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <FaUsers size={14} color={colors.textMuted} />
            <span style={{ fontSize: '13px', color: colors.textMuted, fontWeight: 500 }}>Total Contacts</span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: colors.navy }}>{summary.counts.total}</div>
        </div>

        {/* Leads */}
        <div
          onClick={() => onStageFilterChange?.('leads')}
          style={{
            background: colors.bgCard,
            borderRadius: 14,
            padding: '20px 24px',
            border: `1px solid ${colors.border}`,
            boxShadow: shadows.card,
            cursor: onStageFilterChange ? 'pointer' : 'default',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={(e) => {
            if (onStageFilterChange) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = shadows.medium;
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = shadows.card;
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <FaUserPlus size={14} color={CHART_COLORS.navyLight} />
            <span style={{ fontSize: '13px', color: colors.textMuted, fontWeight: 500 }}>Leads & Prospects</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontSize: '28px', fontWeight: 700, color: colors.navy }}>{leadsTotal}</span>
            <span style={{ fontSize: '12px', color: colors.textMuted }}>
              ({summary.stages.LEAD} + {summary.stages.PROSPECT})
            </span>
          </div>
        </div>

        {/* Customers (Active) */}
        <div
          onClick={() => onStageFilterChange?.('customers')}
          style={{
            background: colors.bgCard,
            borderRadius: 14,
            padding: '20px 24px',
            border: `1px solid ${colors.border}`,
            boxShadow: shadows.card,
            cursor: onStageFilterChange ? 'pointer' : 'default',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={(e) => {
            if (onStageFilterChange) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = shadows.medium;
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = shadows.card;
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <FaCheckCircle size={14} color={CHART_COLORS.success} />
            <span style={{ fontSize: '13px', color: colors.textMuted, fontWeight: 500 }}>Customers</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontSize: '28px', fontWeight: 700, color: colors.navy }}>{customersTotal}</span>
            {summary.customers.new_this_month > 0 && (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: deltaDirection === 'up' ? CHART_COLORS.success : deltaDirection === 'down' ? CHART_COLORS.danger : colors.textMuted,
                }}
              >
                {deltaDirection === 'up' && <FaArrowUp size={10} />}
                {deltaDirection === 'down' && <FaArrowDown size={10} />}
                {deltaDirection === 'flat' && <FaMinus size={10} />}
                +{summary.customers.new_this_month}
              </span>
            )}
          </div>
        </div>

        {/* Partners */}
        <div
          onClick={() => onFilterChange?.('PARTNER')}
          style={{
            background: colors.bgCard,
            borderRadius: 14,
            padding: '20px 24px',
            border: `1px solid ${colors.border}`,
            boxShadow: shadows.card,
            cursor: onFilterChange ? 'pointer' : 'default',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={(e) => {
            if (onFilterChange) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = shadows.medium;
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = shadows.card;
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <FaHandshake size={14} color={CHART_COLORS.purple} />
            <span style={{ fontSize: '13px', color: colors.textMuted, fontWeight: 500 }}>Partners</span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: colors.navy }}>{summary.counts.PARTNER}</div>
        </div>

        {/* Suppliers */}
        <div
          onClick={() => onFilterChange?.('SUPPLIER')}
          style={{
            background: colors.bgCard,
            borderRadius: 14,
            padding: '20px 24px',
            border: `1px solid ${colors.border}`,
            boxShadow: shadows.card,
            cursor: onFilterChange ? 'pointer' : 'default',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={(e) => {
            if (onFilterChange) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = shadows.medium;
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = shadows.card;
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <FaTruck size={14} color={CHART_COLORS.pink} />
            <span style={{ fontSize: '13px', color: colors.textMuted, fontWeight: 500 }}>Suppliers</span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: colors.navy }}>{summary.counts.SUPPLIER}</div>
        </div>
      </div>

      {/* Row 2: Donut Chart + New Customers Bar Chart */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px',
          marginBottom: '16px',
        }}
      >
        {/* Donut Chart: Contacts by Type */}
        <div
          style={{
            background: colors.bgCard,
            borderRadius: 14,
            padding: '20px 24px',
            border: `1px solid ${colors.border}`,
            boxShadow: shadows.card,
          }}
        >
          <div style={{ fontSize: '16px', fontWeight: 600, color: colors.navy, marginBottom: '16px' }}>
            Contacts by Type
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            {/* Donut */}
            <div style={{ position: 'relative', width: '180px', height: '180px' }}>
              <svg width="180" height="180" viewBox="0 0 180 180">
                <circle cx={centerX} cy={centerY} r={radius} fill="none" stroke={colors.bgHover} strokeWidth={strokeWidth} />
                {arcs.map((arc) =>
                  arc.pathD ? (
                    <path
                      key={arc.key}
                      d={arc.pathD}
                      fill="none"
                      stroke={arc.color}
                      strokeWidth={strokeWidth}
                      strokeLinecap="butt"
                      style={{
                        cursor: onFilterChange ? 'pointer' : 'default',
                        opacity: hoveredSegment && hoveredSegment !== arc.key ? 0.5 : 1,
                        transition: 'opacity 0.2s',
                      }}
                      onMouseEnter={() => setHoveredSegment(arc.key)}
                      onMouseLeave={() => setHoveredSegment(null)}
                      onClick={() => onFilterChange?.(arc.key as 'CUSTOMER' | 'PARTNER' | 'SUPPLIER')}
                    />
                  ) : null
                )}
              </svg>
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '24px', fontWeight: 700, color: colors.navy }}>{summary.counts.total}</div>
                <div style={{ fontSize: '11px', color: colors.textMuted }}>Total</div>
              </div>
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
              {donutSegments.map((segment) => (
                <div
                  key={segment.key}
                  onClick={() => onFilterChange?.(segment.key as 'CUSTOMER' | 'PARTNER' | 'SUPPLIER')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: onFilterChange ? 'pointer' : 'default',
                    padding: '6px 8px',
                    borderRadius: 10,
                    background: hoveredSegment === segment.key ? colors.bgPage : 'transparent',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={() => setHoveredSegment(segment.key)}
                  onMouseLeave={() => setHoveredSegment(null)}
                >
                  <div
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '3px',
                      background: segment.color,
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', color: colors.navy, fontWeight: 500 }}>{segment.label}</div>
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: colors.navy }}>{segment.value}</div>
                  <div style={{ fontSize: '12px', color: colors.textLight }}>
                    {((segment.value / total) * 100).toFixed(0)}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bar Chart: New Customers */}
        <div
          style={{
            background: colors.bgCard,
            borderRadius: 14,
            padding: '20px 24px',
            border: `1px solid ${colors.border}`,
            boxShadow: shadows.card,
          }}
        >
          <div style={{ fontSize: '16px', fontWeight: 600, color: colors.navy, marginBottom: '16px' }}>
            New Customers (Last 6 Months)
          </div>

          {displayMonths.length === 0 ? (
            <div
              style={{
                padding: '40px',
                textAlign: 'center',
                color: colors.textLight,
                fontSize: '14px',
                fontStyle: 'italic',
              }}
            >
              No customer data available yet
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {displayMonths.map((month) => {
                const percentage = (month.count / maxMonthCount) * 100;
                return (
                  <div key={month.month} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '50px', fontSize: '12px', color: colors.textMuted, flexShrink: 0 }}>
                      {BusinessPartnerSummaryService.formatMonthLabel(month.month)}
                    </div>
                    <div style={{ flex: 1, height: '20px', background: colors.bgHover, borderRadius: '4px', overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${percentage}%`,
                          height: '100%',
                          background: CHART_COLORS.purple,
                          borderRadius: '4px',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                    <div style={{ width: '24px', fontSize: '13px', fontWeight: 600, color: colors.navy, textAlign: 'right' }}>
                      {month.count}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Row 3: Untouched Customers + Payment Quality */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '16px',
        }}
      >
        {/* Untouched Customers List */}
        <div
          style={{
            background: colors.bgCard,
            borderRadius: 14,
            padding: '20px 24px',
            border: `1px solid ${colors.border}`,
            boxShadow: shadows.card,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ fontSize: '16px', fontWeight: 600, color: colors.navy }}>
              New Customers Without Activity
            </div>
            {summary.untouched_customers.count > 0 && (
              <span
                style={{
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  background: colors.warningLight,
                  color: '#92400e',
                }}
              >
                {summary.untouched_customers.count} customers
              </span>
            )}
          </div>

          {summary.untouched_customers.items.length === 0 ? (
            <div
              style={{
                padding: '24px',
                textAlign: 'center',
                color: colors.success,
                fontSize: '14px',
                background: colors.successLight,
                borderRadius: '8px',
              }}
            >
              All new customers have offers or projects
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {summary.untouched_customers.items.slice(0, 5).map((customer) => (
                <div
                  key={customer.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 14px',
                    background: colors.bgPage,
                    borderRadius: '8px',
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  <div
                    onClick={() => navigate(`/contacts/${customer.id}`)}
                    style={{ cursor: 'pointer', flex: 1 }}
                  >
                    <div style={{ fontSize: '14px', fontWeight: 600, color: colors.navy }}>{customer.name}</div>
                    {customer.city && (
                      <div style={{ fontSize: '12px', color: colors.textMuted }}>{customer.city}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => navigate(`/offers/new?partner_id=${customer.id}`)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        background: CHART_COLORS.navy,
                        color: 'white',
                        border: 'none',
                        borderRadius: 10,
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      <FaFileInvoiceDollar size={10} />
                      Create Offer
                    </button>
                    <button
                      onClick={() => navigate(`/contacts/${customer.id}?tab=projects&action=new`)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        background: colors.bgCard,
                        color: colors.navy,
                        border: `1px solid ${colors.border}`,
                        borderRadius: 10,
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      <FaProjectDiagram size={10} />
                      New Project
                    </button>
                  </div>
                </div>
              ))}
              {summary.untouched_customers.count > 5 && (
                <div style={{ fontSize: '12px', color: colors.textMuted, textAlign: 'center', marginTop: '8px' }}>
                  +{summary.untouched_customers.count - 5} more customers without activity
                </div>
              )}
            </div>
          )}
        </div>

        {/* Payment Quality */}
        <div
          style={{
            background: colors.bgCard,
            borderRadius: 14,
            padding: '20px 24px',
            border: `1px solid ${colors.border}`,
            boxShadow: shadows.card,
          }}
        >
          <div style={{ fontSize: '16px', fontWeight: 600, color: colors.navy, marginBottom: '16px' }}>
            Customer Payment Quality
          </div>

          {paymentTotal === 0 ? (
            <div
              style={{
                padding: '24px',
                textAlign: 'center',
                color: colors.textLight,
                fontSize: '14px',
                fontStyle: 'italic',
              }}
            >
              No invoice data yet
            </div>
          ) : (
            <>
              {/* Stacked Bar */}
              <div
                style={{
                  display: 'flex',
                  height: '24px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  marginBottom: '16px',
                }}
              >
                {paymentSegments.map((seg) => {
                  const width = (seg.value / paymentTotal) * 100;
                  return width > 0 ? (
                    <div
                      key={seg.key}
                      style={{
                        width: `${width}%`,
                        background: seg.color,
                        transition: 'width 0.3s ease',
                      }}
                    />
                  ) : null;
                })}
              </div>

              {/* Legend */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {paymentSegments.map((seg) => (
                  <div key={seg.key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '2px',
                        background: seg.color,
                      }}
                    />
                    <span style={{ flex: 1, fontSize: '13px', color: colors.textMuted }}>{seg.label}</span>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: colors.navy }}>{seg.value}</span>
                    <span style={{ fontSize: '12px', color: colors.textLight }}>
                      ({paymentTotal > 0 ? ((seg.value / paymentTotal) * 100).toFixed(0) : 0}%)
                    </span>
                  </div>
                ))}
              </div>

              {/* No invoices note */}
              {summary.payment_behavior.no_invoices > 0 && (
                <div
                  style={{
                    marginTop: '12px',
                    paddingTop: '12px',
                    borderTop: `1px solid ${colors.border}`,
                    fontSize: '12px',
                    color: colors.textLight,
                  }}
                >
                  {summary.payment_behavior.no_invoices} customers without invoices
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
